import { ScoreIcf, ScorePercent } from "../scores";
import { getMaxScore, TaskPointScoring } from "./scoring";
import { Failure } from "../utils/failure";


export type SumScoringValues = number[];

export class SumScoringValidator {
    private constructor(
        private scoring: TaskPointScoring
    ) { }

    static fromScoring(scoring: TaskPointScoring): SumScoringValidator {
        return new SumScoringValidator(scoring);
    }

    validateArray(rawInput: SumScoringValues): ValidSumScoringValues | ValidSumScoringFailure {
        const numberOfTasks = this.scoring.tasks.length;
        const maxScore = getMaxScore(this.scoring.scoreMapping);
        return ValidSumScoringValues.fromArray(rawInput, numberOfTasks, maxScore);
    }
}

export class ValidSumScoringValues {
    private constructor(
        private values_: SumScoringValues
    ) { }

    static fromArray(rawInput: SumScoringValues, numberOfTasks: number, maximumTaskPoints: number): ValidSumScoringValues | ValidSumScoringFailure {
        if (rawInput.length !== numberOfTasks) {
            return Failure.withContext(
                `Incorrect number of tasks. Expected ${numberOfTasks}, found ${rawInput.length}.`,
                { values: rawInput }
            );
        }

        if (rawInput.some(value => 0 > value || value > maximumTaskPoints || !Number.isInteger(value))) {
            return Failure.withContext(
                `Invalid value found. Only whole numbers from 0 to ${maximumTaskPoints} are allowed.`,
                { values: rawInput }
            );
        }

        return new ValidSumScoringValues(Array.from(rawInput));
    }

    get values(): SumScoringValues {
        return this.values_;
    }
}

export type ValidSumScoringFailure = Failure<{
    values: number[];
}>;

export class SumScoring {
    private constructor(
        private scoringInfo: TaskPointScoring
    ) { }

    static fromScoringInfo(info: TaskPointScoring): SumScoring {
        return new SumScoring(info);
    }

    getValidValues(rawInput: SumScoringValues): ValidSumScoringValues | ValidSumScoringFailure {
        const validator = SumScoringValidator.fromScoring(this.scoringInfo);
        return validator.validateArray(rawInput);
    }

    calculateScores(input: ValidSumScoringValues): SumScores {
        let totalSum = 0;
        const tasks = input.values.map(nextScore => {
            // Update the sum
            totalSum += nextScore;

            const icfLevel = this.scoringInfo.scoreMapping[nextScore];
            return ScoreIcf.fromIcfLevel(icfLevel);
        });

        const numberOfTasks = this.scoringInfo.tasks.length;
        const maximumScore = getMaxScore(this.scoringInfo.scoreMapping);
        const maximumPoints = numberOfTasks * maximumScore;
        const total = ScorePercent.fromFulfillment(totalSum / maximumPoints);

        return {
            total,
            tasks
        };
    }
}

export interface SumScores {
    total: ScorePercent;
    tasks: ScoreIcf[];
}
